// ===================================================================
// 🔐 TROCA OBRIGATÓRIA DE SENHA - Primeiro acesso (admin/analista)
// ===================================================================

import React, { useState, useCallback } from 'react';
import { styles } from '../../styles/globalStyles';
import { Alert } from '../../components/ui/Alert';
import { apiClient } from '../../api/apiClient';
import { validateNewPassword, PASSWORD_POLICY_HINT } from '../../utils/passwordPolicy';

const EyeToggle = ({ visible, onToggle }) => (
  <button
    type="button"
    onClick={onToggle}
    tabIndex={-1}
    style={{
      position: 'absolute',
      right: '10px',
      top: '50%',
      transform: 'translateY(-50%)',
      background: 'none',
      border: 'none',
      cursor: 'pointer',
      padding: '4px',
      color: '#6B7280',
    }}
    title={visible ? 'Ocultar senha' : 'Ver senha'}
  >
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      {visible ? (
        <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24" />
      ) : (
        <><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" /><circle cx="12" cy="12" r="3" /></>
      )}
    </svg>
  </button>
);

export const ChangePasswordScreen = React.memo(({ user, onSuccess, onUseAnotherAccount }) => {
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showCurrent, setShowCurrent] = useState(false);
  const [showNew, setShowNew] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = useCallback(async (e) => {
    e.preventDefault();
    setError('');
    if (!currentPassword) {
      setError('Informe a senha atual');
      return;
    }
    const pwdErr = validateNewPassword(newPassword);
    if (pwdErr) {
      setError(pwdErr);
      return;
    }
    if (newPassword !== confirmPassword) {
      setError('As senhas não coincidem');
      return;
    }
    setLoading(true);
    try {
      const updated = await apiClient.auth.changePassword({
        current_password: currentPassword,
        password: newPassword,
      });
      onSuccess(updated);
    } catch (err) {
      setError(err.message || 'Erro ao alterar senha');
    } finally {
      setLoading(false);
    }
  }, [currentPassword, newPassword, confirmPassword, onSuccess]);

  return (
    <div style={styles.authContainer}>
      <div style={styles.authBox}>
        <h1 style={styles.authTitle}>Alterar senha</h1>
        <p style={{ ...styles.authSubtitle, marginBottom: '24px' }}>
          Por segurança, é obrigatório alterar sua senha no primeiro acesso. Digite a senha atual e escolha uma nova.
        </p>
        <form onSubmit={handleSubmit} style={styles.form}>
          {error && <Alert message={error} type="error" onClose={() => setError('')} />}
          <div style={{ marginBottom: '16px' }}>
            <label style={styles.label}>Senha atual *</label>
            <div style={{ position: 'relative' }}>
              <input
                type={showCurrent ? 'text' : 'password'}
                value={currentPassword}
                onChange={(e) => setCurrentPassword(e.target.value)}
                placeholder="Digite a senha que você recebeu"
                autoComplete="current-password"
                required
                style={{ ...styles.input, paddingRight: '40px' }}
              />
              <EyeToggle visible={showCurrent} onToggle={() => setShowCurrent(v => !v)} />
            </div>
          </div>
          <div style={{ marginBottom: '16px' }}>
            <label style={styles.label}>Nova senha *</label>
            <p style={{ fontSize: 13, color: '#6B7280', margin: '0 0 8px', lineHeight: 1.5 }}>{PASSWORD_POLICY_HINT}</p>
            <div style={{ position: 'relative' }}>
              <input
                type={showNew ? 'text' : 'password'}
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                placeholder="Mínimo 8 caracteres"
                autoComplete="new-password"
                required
                minLength={8}
                style={{ ...styles.input, paddingRight: '40px' }}
              />
              <EyeToggle visible={showNew} onToggle={() => setShowNew(v => !v)} />
            </div>
          </div>
          <div style={{ marginBottom: '24px' }}>
            <label style={styles.label}>Confirmar nova senha *</label>
            <div style={{ position: 'relative' }}>
              <input
                type={showConfirm ? 'text' : 'password'}
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                placeholder="Repita a nova senha"
                autoComplete="new-password"
                required
                minLength={6}
                style={{ ...styles.input, paddingRight: '40px' }}
              />
              <EyeToggle visible={showConfirm} onToggle={() => setShowConfirm(v => !v)} />
            </div>
          </div>
          <button type="submit" disabled={loading} style={{ ...styles.button, ...styles.buttonPrimary, width: '100%' }}>
            {loading ? 'Alterando...' : 'Alterar senha'}
          </button>
          {onUseAnotherAccount && (
            <button
              type="button"
              disabled={loading}
              onClick={onUseAnotherAccount}
              style={{ ...styles.button, ...styles.buttonSecondary, width: '100%', marginTop: '8px' }}
            >
              Entrar com outra conta
            </button>
          )}
        </form>
      </div>
    </div>
  );
});
